/* eslint-disable max-lines,complexity,id-denylist,id-length,max-depth,max-nested-callbacks,no-magic-numbers,prefer-const,custom/todo-format -- Legacy to refactor */
import {
  CHARACTER_VISIBILITY,
  CharacterType,
  ChatRole,
  ConversationFlow,
  CONVIMAGE_INCLUDE_PERSONA,
  CONVIMAGE_LEGACYMODE,
  CONVIMAGE_LORASCALE,
  CONVIMAGE_MODELSTYLE_KEY,
  CONVIMAGE_NSFWMODE,
  CONVIMAGE_USER_NEGATIVE_PROMPT,
  CONVIMAGE_USER_PROMPT_SUFFIX,
  DEFAULT_CHAT_LANGUAGE,
  DEFAULT_INFERENCE_SETTINGS,
  hydrateConversationRulebookHud,
  ICharacter,
  IClientGenerateConversationImageRequest,
  IClientGenerateMessageRequest,
  IConversationMessage,
  IMAGE_MODEL_STYLE,
  INFERENCE_MAX_NEW_TOKENS_KEY,
  INFERENCE_MODEL_KEY,
  INFERENCE_TEMPERATURE_KEY,
  INFERENCE_TOPK_KEY,
  INFERENCE_TOPP_KEY,
  IPersona,
  NotificationType,
  setConversationRulebookSettings,
  TypingMode,
  useAppDispatch,
  useConversation as useConversationShared,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { ConversationMessage, CreateConversationRequest, UpdateConversationLabelResponse } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import useApi from "@/components/useApi";
import { ROUTES } from "@/constants";
import { useAgeVerificationFlags, useRecombee } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useLocalizedNavigation from "@/hooks/useLocalizedNavigation";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import useToast from "@/hooks/useToast";
import { ChatError, type Conversation } from "@/types";
import { getAvatarUrl, isConversationAccessDeniedError, replaceMessageTokens } from "@/utils";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import { ANONYMOUS_CONVERSATION_NAME } from "../components/ChatBubble/constants";
import type { GenerationOptions, IHandleLoadMessages, ILoadMessageAfterGenerate, ILoadMessages, ImageGenerationPayload } from "./types";

const logger = appLogger.withTag("useConversation");

type UseConversationProps = {
  characterId: string;
  character: ICharacter | null;
  conversationId?: string | null;
  name: string;
  setPersonaToChat: (persona: IPersona | null) => void;
  selectedPersona: IPersona | null;
  isDoNotShowChoosePersonaChecked: boolean;
  personas: IPersona[];
  getDefaultPersona: () => IPersona | null | undefined;
  defaultPersonaId: string | null;
  setSelectedPersonaIds: (ids: string[]) => void;
  setIsNewConversation: (isNew: boolean) => void;
  isStoryMode?: boolean;
};

// eslint-disable-next-line max-lines-per-function -- Legacy hook managing all conversation state; its refactoring is tracked separately.
function useConversation({
  characterId,
  character,
  conversationId: initialConversationId = null,
  name = ANONYMOUS_CONVERSATION_NAME,
  setPersonaToChat,
  selectedPersona,
  isDoNotShowChoosePersonaChecked,
  personas,
  getDefaultPersona,
  defaultPersonaId,
  setSelectedPersonaIds,
  setIsNewConversation,
  isStoryMode,
}: UseConversationProps) {
  const { onlyUserId } = useUserInfo();
  const resolvedUserName = name || ANONYMOUS_CONVERSATION_NAME;
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const { userChatLanguage } = useUserSettings();
  const { sendUserInteractionCartAddition, recombeeUserId } = useRecombee();
  const navigate = useLocalizedNavigation();
  const localizePath = useLocalizedPathname();
  const location = useLocation();
  const { isAgeVerificationRequired } = useAgeVerificationFlags();
  const [stateMessages, setStateMessages] = useState<Conversation | null>(null);
  const { jsonApiService, callApi: callApiMessages, isLoading: isLoadingMessages } = useApi();
  const { callApi: callApiCreateConversation, errorData, resetError: resetApiRequestError } = useApi<{ id: string }>();
  const { callApi: callApiRemoveMessage, isLoading: isRemoveMessageLoading } = useApi();
  const { callApi: callApiLabel } = useApi();
  const { callApi: callApiUpdateCloneTitle, isLoading: isCloneUpdateTitleLoading } = useApi<{ result: UpdateConversationLabelResponse }>();

  const [stateConversationId, setStateConversationId] = useState(initialConversationId !== "new" ? initialConversationId : null);
  const [stateIsTyping, setStateIsTyping] = useState<TypingMode | null>(null);
  const [stateConversation, setStateConversation] = useState<IConversationMessage[]>([]);
  const [stateAltMsgs, setStateAltMsgs] = useState<IConversationMessage[]>([]);
  const [stateAltMsgPosition, setStateAltMsgPosition] = useState(0);
  const [stateCappedMessageId, setStateCappedMessageId] = useState<string | null | undefined>(undefined);
  const [stateRemoveMessageIdx, setStateRemoveMessageIdx] = useState<number | null>(null);
  const [stateIsRemovalMode, setStateIsRemovalMode] = useState(false);
  const [stateLastMessageId, setStateLastMessageId] = useState<string | null>(null); //used to only retrieve partial conversation
  const [stateInvalidUrlError, setStateInvalidUrlError] = useState<{ message: string } | null>(null);
  const [stateLastGenerationOptions, setStateLastGenerationOptions] = useState<GenerationOptions | null>(null); //used in case of failure to allow retrying
  const { pagePath } = useLocalizedLocation();
  const [stateConversationLabel, setStateConversationLabel] = useState("");
  const [stateConversationCreatedAt, setStateConversationCreatedAt] = useState<number | null>(null);
  const [stateConversationNumMessages, setStateConversationNumMessages] = useState<number | null>(null);
  const [stateIsMessagesLoading, setStateIsMessagesLoading] = useState(true);
  const [stateIsChoosePersona, setStateIsChoosePersona] = useState(false);
  const [stateIsLastMessageIdDeleted, setStateIsLastMessageIdDeleted] = useState(false);
  const [stateVoiceId, setStateVoiceId] = useState("");

  // Refs to stabilize unstable props from usePersona (prevents infinite re-render loops)
  // TODO: [NX-7102] Remove these refs once we've refactored the usePersona hook to use a context instead of props
  const refSetPersonaToChat = useRef(setPersonaToChat);
  const refGetDefaultPersona = useRef(getDefaultPersona);
  const refSetSelectedPersonaIds = useRef(setSelectedPersonaIds);
  const refSetIsNewConversation = useRef(setIsNewConversation);
  const refPersonas = useRef(personas);

  // Keep refs in sync with latest prop values
  useEffect(() => {
    refSetPersonaToChat.current = setPersonaToChat;
    refGetDefaultPersona.current = getDefaultPersona;
    refSetSelectedPersonaIds.current = setSelectedPersonaIds;
    refSetIsNewConversation.current = setIsNewConversation;
    refPersonas.current = personas;
  });

  const {
    onUserGenerateMessage,
    onUserGenerateConversationImage,
    onUserUpdateMessage,
    onUserDeleteMessageImage,
    errorMessage: conversationErrorMessage,
    errorTitle: conversationErrorTitle,
    errorType,
    errorCode: conversationErrorCode,
    resetError: resetConversationError,
    isLoading,
    loadingType,
  } = useConversationShared({ characterId, conversationId: stateConversationId });
  const errorCode = errorData?.message || errorData?.error || conversationErrorCode;
  const shouldShowErrorMessageInBubble =
    errorType === ConversationFlow.GENERATE_MESSAGE ||
    errorType === ConversationFlow.UPDATE_MESSAGE ||
    errorType === ConversationFlow.GENERATE_CONVERSATION_IMAGE;
  const errorMessage = (shouldShowErrorMessageInBubble && conversationErrorMessage) || (!!errorData?.error && t("error:fallbackError")) || "";
  const errorTitle = (shouldShowErrorMessageInBubble && conversationErrorTitle) || "";
  const isImageDeleteFromMessageLoading = isLoading && loadingType === ConversationFlow.DELETE_MESSAGE_IMAGE;

  const isPublicBot = character?.visibility === CHARACTER_VISIBILITY.PUBLIC;

  const resetError = useCallback(() => {
    setStateIsTyping(null);
    resetApiRequestError();
    resetConversationError();
  }, [resetApiRequestError, resetConversationError]);

  const onNewConversation = useCallback(async () => {
    resetError();
    setStateConversationId(null);
    setStateMessages(null);
    setStateConversation([]);
    setStateAltMsgs([]);
    setStateAltMsgPosition(0);
    setStateLastMessageId(null);
    setStateVoiceId("");
    setStateCappedMessageId(null);
  }, [resetError]);

  //Change the display url if the conversation is new
  useEffect(() => {
    const hasInitialConversationWithTextMessages = stateConversation.length === 3;
    const hasInitialConversationWithImage = stateConversation.length === 2;
    const pathSegments = pagePath?.split("/") || [];
    const currentConversationIdInUrl = pathSegments[3];

    // Only update URL if we have a new conversationId and it's not already in the URL
    // Use window.history.replaceState to avoid triggering route changes and duplicate API calls
    if (
      (hasInitialConversationWithTextMessages || hasInitialConversationWithImage) &&
      stateConversationId &&
      stateConversationId !== initialConversationId &&
      currentConversationIdInUrl !== stateConversationId &&
      !isLoadingMessages
    ) {
      const pathPrefix = isStoryMode ? ROUTES.story : ROUTES.chat;
      const newUrl = `${pathPrefix}/${characterId}/${stateConversationId}`;
      // The entry's state stays: the router's own index and the back guard on the chat that opened the tab.
      window.history.replaceState(window.history.state, "", localizePath(newUrl));
    }
  }, [stateConversation, stateConversationId, characterId, isStoryMode, pagePath, initialConversationId, isLoadingMessages, localizePath]);

  const displayError = useCallback(
    (message?: string) => {
      displayToast(message ? { message, type: NotificationType.error } : { type: NotificationType.error });
    },
    [displayToast],
  );

  const displaySuccess = useCallback(
    (message?: string) => {
      displayToast(message ? { message, type: NotificationType.success } : { type: NotificationType.success });
    },
    [displayToast],
  );

  const handleDeleteMessageImage = async (messageId: string) => {
    const { data: updatedMessage, error } = await onUserDeleteMessageImage(messageId);
    if (error) {
      displayError(error);
      return;
    }
    if (updatedMessage) {
      setStateConversation((prevMessages: IConversationMessage[]) => {
        return prevMessages.map((m: IConversationMessage) => (m.id === messageId ? { ...(updatedMessage as IConversationMessage) } : m));
      });
    }
  };

  const dispatch = useAppDispatch();
  const handleLoadedMessages = useCallback(
    ({ res, last_id: lastId, generatedMessage }: IHandleLoadMessages) => {
      setStateIsLastMessageIdDeleted(false);
      //we look in all messages if we have is_last_id as a promerty of messages
      if (res?.messages?.length > 0) {
        refSetPersonaToChat.current(res?.userPersona ?? null);
        const lastMessage = res.messages.find((m: IConversationMessage) => m.is_last_id);
        if (lastMessage) {
          setStateLastMessageId(lastMessage?.id ?? null);
        } else {
          setStateLastMessageId(null);
        }
      } else {
        if (!isDoNotShowChoosePersonaChecked && refPersonas.current?.length > 1) {
          setStateIsChoosePersona(true);
          if (defaultPersonaId) {
            refSetSelectedPersonaIds.current([defaultPersonaId]);
          }
          refSetIsNewConversation.current(true);
        }
        const defaultPersona = refGetDefaultPersona.current();
        if (defaultPersona) {
          refSetPersonaToChat.current(defaultPersona);
        }
      }
      //if we have a last_id we add messages  to the conversation otherwise we replace them
      if (lastId) {
        setStateMessages((prevMessages: Conversation | null) => {
          if (!prevMessages) {
            return prevMessages;
          }

          return {
            ...prevMessages,
            messages: [...(prevMessages?.messages ?? []), ...res.messages],
          };
        });
      } else {
        const updatedMessages = res;

        // Preserve image_prompt from generated message if the last message is an image without image_prompt
        const recentMessage = updatedMessages.messages?.[0];

        if (recentMessage?.image_url && !recentMessage.image_prompt && generatedMessage?.image_prompt) {
          updatedMessages.messages[0] = {
            ...recentMessage,
            image_prompt: generatedMessage.image_prompt,
          };
        }

        setStateMessages(updatedMessages);
      }
      if (res?.conversation_id) {
        setStateConversationId(res.conversation_id);
      }
      if (res?.label) {
        setStateConversationLabel(res.label);
      }
      // Set capped message ID: fresh loads always update (to value or null),
      // pagination only updates if present (preserves existing capped state)
      if (!lastId || res?.capped_message_id) {
        setStateCappedMessageId(res?.capped_message_id ?? null);
      }
      // Hydrate conversation-level Rulebook settings on the initial load only — pagination
      // responses carry the same settings and must not clobber optimistic updates.
      if (!lastId && res?.conversation_id) {
        dispatch(setConversationRulebookSettings({ conversationId: res.conversation_id, settings: res.rulebook ?? {} }));
        dispatch(
          hydrateConversationRulebookHud({
            conversationId: res.conversation_id,
            personaId: res.userPersona?.id,
            rulebook: res.rulebook ?? null,
            rulebookHud: res.rulebook_hud ?? null,
          }),
        );
      }
      setStateVoiceId(res?.voice_id ?? "");
      setStateConversationCreatedAt(res?.createdAt ?? null);
      setStateConversationNumMessages(res?.num_messages ?? null);
    },
    [dispatch, isDoNotShowChoosePersonaChecked, defaultPersonaId],
  );

  // Function to load messages
  const loadMessages = useCallback(
    async (props?: ILoadMessages) => {
      if (!characterId) {
        return;
      }
      const { limit = 50, last_id: lastId = "", updatedConversationId = "", generatedMessage } = props ?? {};
      const conversationIdVal = updatedConversationId !== "" ? updatedConversationId : initialConversationId;
      try {
        const res = await callApiMessages(jsonApiService.getCharacterMessages, characterId, conversationIdVal, {
          limit: limit.toString(),
          last_id: lastId ?? "",
        });
        handleLoadedMessages({ res: res as Conversation, last_id: lastId, generatedMessage });
      } catch (error: unknown) {
        if (!isConversationAccessDeniedError(error)) {
          logger.error("Error when getting character's messages:", error, {
            error_type: ChatError.MESSAGE_FETCH,
            user_id: onlyUserId,
            chatbot_id: characterId,
            message: (error as Error).message,
          });
        }
        if (error && typeof error === "object" && "message" in error) {
          setStateInvalidUrlError(error as { message: string });
        } else {
          setStateInvalidUrlError({ message: "An unknown error occurred." });
        }
      } finally {
        setStateIsMessagesLoading(false);
      }
    },
    [callApiMessages, characterId, initialConversationId, jsonApiService.getCharacterMessages, handleLoadedMessages, onlyUserId],
  );

  // useEffect to load initial messages
  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  //fill conversation with messages
  useEffect(() => {
    if (character && !isLoadingMessages) {
      const replaceMessageArrayTokens = (messages: IConversationMessage[]) => {
        return messages.map((m: IConversationMessage) => {
          return {
            ...m,
            content: replaceMessageTokens(m.content, {
              characterName: character?.name,
              userName: resolvedUserName,
            }),
          };
        });
      };
      const loadConversation = async () => {
        if (stateMessages?.conversation_id) {
          stateMessages.messages = replaceMessageArrayTokens(stateMessages.messages);
          //if we have an alternative message, we move them all to the alternativeMessages array
          const prevId = stateMessages.messages.find((m: IConversationMessage) => m.is_alternative)?.prev_id;
          //we want to sort based on createdAt since we receive descending order
          const conversationMessages = (
            stateMessages.messages.sort((a: IConversationMessage, b: IConversationMessage) => {
              if (a.createdAt < b.createdAt) {
                return -1;
              }
              if (a.createdAt > b.createdAt) {
                return 1;
              }
              return 0;
            }) || []
          ).filter((message: IConversationMessage) => message.prev_id !== prevId || !prevId);
          const altMsgs = (stateMessages.messages || []).filter((message: IConversationMessage) => message.prev_id === prevId && prevId);

          const greetingCharacterId = character?.greeting_character_id;
          const messagesWithGreetingCharacter = greetingCharacterId
            ? conversationMessages.map((conversationMessage: IConversationMessage, index: number) =>
                index === 0 && conversationMessage.role === ChatRole.BOT && !conversationMessage.character_id
                  ? { ...conversationMessage, character_id: greetingCharacterId }
                  : conversationMessage,
              )
            : conversationMessages;
          setStateConversation(messagesWithGreetingCharacter);
          setStateAltMsgs(altMsgs);
        }
      };

      void loadConversation();
    }
  }, [stateMessages, isLoadingMessages, character, name]);

  const addBotTurn = useCallback(
    (content: string, subCharacterId?: string | undefined) => {
      const message = replaceMessageTokens(content, {
        characterName: character?.name,
        userName: resolvedUserName,
      });

      setStateConversation((prevConversation: IConversationMessage[]) => [
        ...prevConversation,
        //@ts-expect-error Need to create new type with only the properties we need
        {
          role: ChatRole.BOT,
          content: message,
          character_id: subCharacterId ?? "",
          ...(character?.greeting_audio_url ? { greeting_audio_url: character.greeting_audio_url } : {}),
        } as IConversationMessage,
      ]);
    },
    [character?.name, character?.greeting_audio_url, name],
  );

  //Add greeting message if not past conversation
  useEffect(() => {
    if (character?.greeting && !isLoadingMessages && !stateConversation.length && (stateConversationId === "new" || !stateConversationId)) {
      if (character?.greeting_character_id && Array.isArray(character?.sub_characters)) {
        const subCharacter = character.sub_characters.find((sub: ICharacter) => sub.id === character.greeting_character_id);
        addBotTurn(character?.greeting, subCharacter?.id);
      } else {
        addBotTurn(character?.greeting, undefined);
      }
    }
  }, [
    character?.greeting,
    isLoadingMessages,
    stateConversationId,
    addBotTurn,
    stateConversation,
    character?.name,
    name,
    character?.greeting_character_id,
    character?.sub_characters,
  ]);

  //Change the display url if the conversation is no longer new
  useEffect(() => {
    if (initialConversationId === "new") {
      if (stateConversationId) {
        navigate(`/chat/${characterId}`, {
          state: location.state,
          replace: true,
        });
      }
    }
  }, [stateConversationId, initialConversationId, characterId, location.state, navigate]);

  const loadMoreMessages = useCallback(async () => {
    if (!stateLastMessageId && !stateIsLastMessageIdDeleted) {
      return;
    }
    await loadMessages({ last_id: stateLastMessageId });
  }, [loadMessages, stateLastMessageId, stateIsLastMessageIdDeleted]);

  const handleRegenerateAndAltMsgs = useCallback(
    <T extends IClientGenerateMessageRequest | ImageGenerationPayload>(props?: {
      regenerate: boolean;
      conv: IConversationMessage[];
      alt: IConversationMessage[];
      payload: T;
    }) => {
      const { regenerate, conv = [], alt = [], payload = null } = props ?? {};

      if (!payload) {
        return { regenerate, conv, alt, payload };
      }

      if (!regenerate && stateAltMsgs?.length > 0) {
        //we remove any alt messages other than the one selected to continue the conversation
        payload.alt_message_id = stateAltMsgs[stateAltMsgPosition]?.id;
        conv.push(stateAltMsgs[stateAltMsgPosition]);
        setStateAltMsgs([]);
        setStateAltMsgPosition(0);
        setStateConversation(conv);
      }
      if (regenerate) {
        if (!stateAltMsgs?.length) {
          //first regeneration, so we move last message to alts
          const lastMsg = conv.pop();
          alt.push(lastMsg as IConversationMessage);
          setStateAltMsgs(alt);
          setStateAltMsgPosition(alt.length);
          setStateConversation(conv);
        }
        payload.prev_id = alt[0].prev_id;
      }
      return { regenerate, conv, alt, payload };
    },
    [stateAltMsgPosition, stateAltMsgs, setStateAltMsgPosition, setStateAltMsgs, setStateConversation],
  );

  const handleLoadMessageAfterGenerate = useCallback(
    ({ conversationIdFromMsgRes, generatedMessage, autopilot }: ILoadMessageAfterGenerate) => {
      setStateConversationId(conversationIdFromMsgRes);
      // load messages when user send a message after starting new chat. So, that other messages can have there id's and it can be used for playing voice or doing other future operations.
      if (!stateConversationId && !autopilot) {
        void loadMessages({ updatedConversationId: conversationIdFromMsgRes, generatedMessage });
      }
    },
    [stateConversationId, loadMessages],
  );

  const generateImage = useCallback(
    async ({ regenerate, imagePrompt }: { regenerate: boolean; imagePrompt: string }) => {
      setTimeout(() => {
        setStateIsTyping(TypingMode.BOT);
      }, 300);

      let payload: ImageGenerationPayload | null = {
        conversation_id: stateConversationId,
        character_id: characterId,
        image_prompt: imagePrompt,
        model_style: "",
        nsfw_mode: true,
        legacy_mode: false,
        lora_scale: 0.8,
      };

      //we rely on local storage until redux persist is implemented
      payload.model_style = typedLocalStorageService.getItem(CONVIMAGE_MODELSTYLE_KEY) ?? "";
      payload.nsfw_mode = !isAgeVerificationRequired && typedLocalStorageService.getItem(CONVIMAGE_NSFWMODE) === "true";
      payload.legacy_mode = typedLocalStorageService.getItem(CONVIMAGE_LEGACYMODE) === "true";
      const loraScale = parseFloat(typedLocalStorageService.getItem(CONVIMAGE_LORASCALE) ?? "0.8");

      if (loraScale >= 0 && loraScale <= 1) {
        payload.lora_scale = loraScale;
      }
      payload.user_prompt_suffix = typedLocalStorageService.getItem(CONVIMAGE_USER_PROMPT_SUFFIX) ?? "";
      payload.user_negative_prompt = typedLocalStorageService.getItem(CONVIMAGE_USER_NEGATIVE_PROMPT) ?? "";
      payload.user_persona_id = selectedPersona?.id ?? null;
      const isQwenStyle = payload.model_style === IMAGE_MODEL_STYLE.QWEN || payload.model_style === IMAGE_MODEL_STYLE.QWEN_MALE;
      const hasPersonaAvatar = !!selectedPersona?.avatar_url;
      const shouldIncludePersona = typedLocalStorageService.getItem(CONVIMAGE_INCLUDE_PERSONA) === "true";
      payload.include_user_persona_image = shouldIncludePersona && hasPersonaAvatar && isQwenStyle;

      const previousConversation = [...stateConversation];
      const previousAltMessages = [...stateAltMsgs];
      const previousAltMsgPosition = stateAltMsgPosition;

      let conv = [...stateConversation];
      let alt = regenerate ? [...stateAltMsgs] : [];

      //logic to manage alternatives
      ({ conv, alt, payload } = handleRegenerateAndAltMsgs<ImageGenerationPayload>({ regenerate, conv, alt, payload }));
      setStateConversation(conv);

      const { data: res, error } = await onUserGenerateConversationImage(payload as IClientGenerateConversationImageRequest);

      if (error) {
        setStateConversation(previousConversation);
        setStateAltMsgs(previousAltMessages);
        setStateAltMsgPosition(previousAltMsgPosition);
        setStateIsTyping(null);
        return;
      }

      const message = res?.message;

      if (!message) {
        setStateIsTyping(null);
        return;
      }

      const convoIdFromResponse = message?.conversation_id;
      if (convoIdFromResponse) {
        handleLoadMessageAfterGenerate({ conversationIdFromMsgRes: convoIdFromResponse, generatedMessage: message });
      }
      setStateIsTyping(null);
      resetError();
      if (regenerate) {
        setStateAltMsgs([...alt, message]);
        setStateAltMsgPosition(alt.length);
      } else {
        //we update the id of the last message in the conversation if missing
        const lastMessage = conv[conv.length - 1];
        if (lastMessage && !lastMessage.id) {
          lastMessage.id = message?.prev_id;
        }
        setStateConversation([...conv, message]);
      }
    },
    [
      stateConversationId,
      characterId,
      stateConversation,
      stateAltMsgs,
      stateAltMsgPosition,
      handleRegenerateAndAltMsgs,
      resetError,
      isAgeVerificationRequired,
      handleLoadMessageAfterGenerate,
      selectedPersona?.id,
      selectedPersona?.avatar_url,
      onUserGenerateConversationImage,
    ],
  );

  const generateMessage = useCallback(
    // eslint-disable-next-line max-lines-per-function -- Complex message generation logic; its refactoring is tracked separately.
    async (props?: {
      content: string;
      resubmit: boolean;
      regenerate: boolean;
      autopilot: boolean;
      continueChat: boolean;
      imageKey?: string;
      abortSignal: globalThis.AbortSignal;
      subCharacterId?: string | null;
    }) => {
      const { resubmit = false, regenerate = false, autopilot = false, continueChat = false, imageKey, abortSignal, subCharacterId } = props ?? {};
      let content = props?.content;

      // Delay showing the typing indicator so a fast response does not flash it.
      // Kept in a ref so it can be cancelled once the request settles; otherwise
      // a fast failure lets this fire after typing was reset, leaving the input
      // stuck in a disabled/typing state.
      const typingIndicatorTimeout = setTimeout(() => {
        setStateIsTyping(autopilot ? TypingMode.AUTOPILOT : TypingMode.BOT);
      }, 300);
      let conv = [...stateConversation];
      let alt = regenerate ? [...stateAltMsgs] : [];
      let payload: IClientGenerateMessageRequest | null = {
        inference_model: "default",
        inference_settings: {
          max_new_tokens: DEFAULT_INFERENCE_SETTINGS?.maxNewTokens,
          temperature: DEFAULT_INFERENCE_SETTINGS.temperature,
          top_p: DEFAULT_INFERENCE_SETTINGS.topP,
          top_k: DEFAULT_INFERENCE_SETTINGS.topK,
        },
        autopilot,
        continue_chat: continueChat,
        message: "",
        abortSignal,
      };
      payload.inference_model = typedLocalStorageService.getItem(INFERENCE_MODEL_KEY) || "default";
      payload.inference_settings = {
        max_new_tokens: parseInt(typedLocalStorageService.getItem(INFERENCE_MAX_NEW_TOKENS_KEY) ?? "") ?? DEFAULT_INFERENCE_SETTINGS?.maxNewTokens,
        temperature: parseFloat(typedLocalStorageService.getItem(INFERENCE_TEMPERATURE_KEY) ?? "") ?? DEFAULT_INFERENCE_SETTINGS.temperature,
        top_p: parseFloat(typedLocalStorageService.getItem(INFERENCE_TOPP_KEY) ?? "") ?? DEFAULT_INFERENCE_SETTINGS.topP,
        top_k: parseFloat(typedLocalStorageService.getItem(INFERENCE_TOPK_KEY) ?? "") ?? DEFAULT_INFERENCE_SETTINGS.topK,
      };
      //We handle subCharacters for group chat ONLY
      if (character?.type === CharacterType.META && subCharacterId !== undefined && subCharacterId !== null) {
        payload.sub_character_id = subCharacterId;
      }

      if (userChatLanguage !== DEFAULT_CHAT_LANGUAGE) {
        payload.language = userChatLanguage;
      }
      if (autopilot) {
        payload.autopilot = true;
      }
      if (continueChat) {
        payload.continue_chat = true;
      }
      if (isStoryMode) {
        payload.story_mode = true;
      }
      //logic to manage alternatives
      ({ conv, alt, payload } = handleRegenerateAndAltMsgs({ regenerate, conv, alt, payload }));
      if (!regenerate && !autopilot && !continueChat && payload) {
        let previewUrl: string | undefined;
        if (resubmit) {
          // Getting last user message of conversation to request again.
          let lastMsg = conv[conv.length - 1];
          if (lastMsg?.role === ChatRole.BOT) {
            conv.pop();
            lastMsg = conv[conv.length - 1];
          }
          content = lastMsg.content ?? "";
        } else if (!autopilot) {
          previewUrl = imageKey ? (getAvatarUrl(imageKey, { returnWithOriginalRatio: true }) as string | null | undefined) || undefined : undefined;
          conv.push({ role: ChatRole.USER, content, image_url: previewUrl } as IConversationMessage);
        }
        payload.message = content ?? "";
      }

      if (imageKey && payload) {
        payload.image_key = imageKey;
      }

      if (!resubmit) {
        setStateConversation(conv);
      }
      if (abortSignal && payload) {
        payload.abortSignal = abortSignal;
      }

      const { data: res, errorPayload } = await onUserGenerateMessage(payload as IClientGenerateMessageRequest);
      // Request settled: cancel the pending typing indicator so a fast failure
      // cannot re-enable typing (which would keep the composer disabled).
      clearTimeout(typingIndicatorTimeout);

      const convoIdFromResponse = res?.message?.conversation_id;
      if (convoIdFromResponse) {
        handleLoadMessageAfterGenerate({ conversationIdFromMsgRes: convoIdFromResponse, autopilot });
      }

      if (resubmit && res) {
        resetError();
        setStateConversation(conv);
      }

      setStateIsTyping(null);
      if (autopilot) {
        return res?.message?.content || null;
      }
      if (res?.message) {
        if (res?.capped_message_id) {
          setStateCappedMessageId(res?.capped_message_id);
        }
        if (regenerate) {
          setStateAltMsgs([...alt, res?.message]);
          setStateAltMsgPosition(alt.length);
        } else {
          if (conv && conv.length > 0) {
            // Update the id of the last message in the conversation if missing
            conv[conv.length - 1].id = conv[conv.length - 1]?.id || res?.message?.prev_id;
          } else {
            logger.debug("Cannot update id: 'conv' is empty or undefined");
          }
          setStateConversation([...conv, res.message]);
        }

        // Only send cart_addition if view_portion feature flag is disabled
        // When view_portion is enabled, tracking is handled by useChatSessionTracking
        if (conv.filter((item: IConversationMessage) => item.role === "user").length === 3 && isPublicBot && !regenerate && !continueChat) {
          await sendUserInteractionCartAddition(recombeeUserId, characterId, { amount: 3 });
        }

        return res.message;
      }

      return { sendFailed: true as const, errorCode: errorPayload?.errorCode ?? null };
    },
    [
      characterId,
      stateConversation,
      stateAltMsgs,
      recombeeUserId,
      handleRegenerateAndAltMsgs,
      resetError,
      sendUserInteractionCartAddition,
      isPublicBot,
      isStoryMode,
      character?.type,
      handleLoadMessageAfterGenerate,
      onUserGenerateMessage,
      userChatLanguage,
    ],
  );

  const generate = useCallback(
    async (props?: GenerationOptions) => {
      let {
        type,
        content = "",
        resubmit = false,
        regenerate = false,
        autopilot = false,
        image_prompt = "",
        image_key,
        continue_chat = false,
        abortSignal,
        subCharacterId,
      } = props ?? {};
      if (errorMessage || errorCode) {
        resetError();
      }
      if (resubmit) {
        ({
          type,
          content = "",
          regenerate = false,
          autopilot = false,
          image_prompt = "",
          image_key,
          subCharacterId,
        } = (stateLastGenerationOptions as GenerationOptions) || {});
      }
      //we'll determine the type dynamically to simplify usage
      if (!type) {
        //The type is text if we have content or it's an autopilot request
        if (content || autopilot) {
          type = "text";
        }
        //if it's a regeneration, we need to determine the time based on presence of an image
        if (regenerate) {
          //if we have alt messages, we look at the first one
          if (stateAltMsgs?.length > 0) {
            if (stateAltMsgs[0]?.image_url) {
              type = "image";
            }
            //we look at the last message in the conversation
          } else if (stateConversation[stateConversation.length - 1]?.image_url) {
            type = "image";
          }
        }
        if (!type) {
          type = "text";
        }
      }
      //we set the last generation options in case of an error
      if (!resubmit) {
        setStateLastGenerationOptions({ type, content, regenerate, autopilot, image_prompt, continue_chat, subCharacterId, image_key });
      }

      let generatedResponse;
      if (type === "text") {
        generatedResponse = await generateMessage({
          content,
          resubmit,
          regenerate,
          autopilot,
          continueChat: continue_chat,
          imageKey: image_key,
          abortSignal: abortSignal as globalThis.AbortSignal,
          subCharacterId,
        });
      } else if (type === "image") {
        generatedResponse = await generateImage({ regenerate, imagePrompt: image_prompt });
      }
      return generatedResponse;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- errorCode/errorMessage are only checked to trigger resetError, not used in callback logic.
    [stateLastGenerationOptions, stateAltMsgs, stateConversation, generateImage, generateMessage, resetError, stateConversationId],
  );

  const changeAltMsgPosition = useCallback(
    (position: number) => {
      let newPos = stateAltMsgPosition + position;
      if (newPos < 0) {
        newPos = 0;
      }
      if (newPos >= stateAltMsgs.length) {
        newPos = stateAltMsgs.length - 1;
      }
      setStateAltMsgPosition(newPos);
    },
    [stateAltMsgPosition, stateAltMsgs],
  );

  const sendRating = useCallback(
    async (messageId: string, label: string) => {
      try {
        await callApiLabel(jsonApiService.submitFeedback, {
          messageId,
          label,
        });

        if (stateAltMsgs?.length) {
          const newAltMsgs = [...stateAltMsgs];
          const msg = newAltMsgs.find((message: IConversationMessage) => message?.id === messageId);
          if (msg) {
            const updatedMsg = { ...msg, rating: label };
            const index = newAltMsgs.indexOf(msg);
            newAltMsgs[index] = updatedMsg;
            setStateAltMsgs(newAltMsgs);
          }
          return;
        }

        const newConv = [...stateConversation];
        const msg = newConv.find((message: IConversationMessage) => message?.id === messageId);
        if (msg) {
          const updatedMsg = { ...msg, rating: label };
          const index = newConv.indexOf(msg);
          newConv[index] = updatedMsg;
          setStateConversation(newConv);
        }
      } catch (error: unknown) {
        logger.error("Error while rating chat message", error, {
          error_type: ChatError.MESSAGE_RATING,
          user_id: onlyUserId,
          chatbot_id: characterId,
          message: (error as Error).message,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- characterId/onlyUserId only used in error logging, not callback behavior.
    [stateAltMsgs, stateConversation, callApiLabel, jsonApiService.submitFeedback],
  );

  const removeMessages = async () => {
    if (stateRemoveMessageIdx === null || stateRemoveMessageIdx === undefined) {
      return;
    }

    let messagesToRemove = stateConversation.slice(stateRemoveMessageIdx).map((m: IConversationMessage) => m?.id);
    //if we have altMessage, we also need to remove them
    messagesToRemove = [...messagesToRemove, ...stateAltMsgs.map((m: IConversationMessage) => m?.id)];
    // Dedupe to prevent duplicate delete requests
    messagesToRemove = [...new Set(messagesToRemove)];

    const newConv = stateConversation.slice(0, stateRemoveMessageIdx);
    callApiRemoveMessage(jsonApiService.deleteChatConversation, stateConversationId as string, messagesToRemove)
      .then(() => {
        if (messagesToRemove?.includes(stateLastMessageId ?? "")) {
          setStateIsLastMessageIdDeleted(true);
          setStateLastMessageId(null);
        }
        setStateRemoveMessageIdx(null);
        setStateIsRemovalMode(false);
        setStateAltMsgs([]);
        setStateAltMsgPosition(0);
        setStateConversation(newConv);
        setStateMessages((prev: Conversation | null) => {
          if (!prev) {
            return prev;
          }

          return { ...prev, messages: newConv };
        });
      })
      .catch((err: Error) => {
        logger.error("Error while removing messages", err, {
          error_type: ChatError.MESSAGE_DELETE,
          user_id: onlyUserId,
          chatbot_id: characterId,
          message: err?.message,
        });
      });
  };

  const deleteSingleMessage = async (messageId: string) => {
    if (!messageId || !stateConversationId) {
      return;
    }

    // Check if message is in alt messages
    const altMsgIndex = stateAltMsgs.findIndex((m: IConversationMessage) => m?.id === messageId);
    const isAltMessage = altMsgIndex !== -1;

    // Check if message is in main conversation
    const messageToDelete = stateConversation.find((m: IConversationMessage) => m?.id === messageId);

    if (!messageToDelete && !isAltMessage) {
      return;
    }

    return callApiRemoveMessage(jsonApiService.deleteChatConversation, stateConversationId, [messageId])
      .then(() => {
        if (messageId === stateLastMessageId) {
          setStateIsLastMessageIdDeleted(true);
          setStateLastMessageId(null);
        }

        if (isAltMessage) {
          // Remove from alt messages using functional update to avoid stale closure
          setStateAltMsgs((prevAltMsgs: IConversationMessage[]) => {
            const newAltMsgs = prevAltMsgs.filter((m: IConversationMessage) => m?.id !== messageId);
            // Adjust position if needed
            setStateAltMsgPosition((prevPos: number) => {
              if (newAltMsgs.length === 0) {
                return 0;
              }
              if (prevPos >= newAltMsgs.length) {
                return newAltMsgs.length - 1;
              }
              return prevPos;
            });
            return newAltMsgs;
          });
        } else {
          // Remove from main conversation using functional updates to avoid stale closure
          setStateConversation((prevConv: IConversationMessage[]) => prevConv.filter((m: IConversationMessage) => m?.id !== messageId));
          setStateMessages((prev: Conversation | null) => {
            if (!prev) {
              return prev;
            }
            return { ...prev, messages: prev.messages.filter((m: IConversationMessage) => m?.id !== messageId) };
          });
        }
      })
      .catch((err: Error) => {
        logger.error("Error while deleting single message", err, {
          error_type: ChatError.MESSAGE_DELETE,
          user_id: onlyUserId,
          chatbot_id: characterId,
          message: err?.message,
        });
      });
  };

  const handleCloneTitleUpdate = async (title: string) => {
    const payload = { label: title };
    try {
      const response = await callApiUpdateCloneTitle(jsonApiService.updateResource, `conversations/${stateConversationId}/label`, "", payload);
      setStateConversationLabel(response?.result?.label ?? "");
      displaySuccess();
    } catch (error: unknown) {
      logger.error("error while updating conversation title", error, {
        error_type: ChatError.CONVERSATION_TITLE_UPDATE,
        user_id: onlyUserId,
        chatbot_id: characterId,
        message: (error as Error).message,
      });
      displayError();
    }
  };
  // used when new conversation is created while editing greetings
  const createConversation = async (messages: ConversationMessage[]) => {
    const payload: CreateConversationRequest = {
      messages,
      user_persona_id: selectedPersona?.id,
    };
    try {
      const { id } =
        (await callApiCreateConversation(
          jsonApiService.createResource,
          `characters/${characterId}/conversations`,
          payload as Record<string, unknown>,
        )) || {};
      const pathPrefix = isStoryMode ? ROUTES.story : ROUTES.chat;
      if (!id) {
        throw new Error("Failed to return conversation id");
      }
      setStateConversationId(id);
      navigate(`${pathPrefix}/${characterId}/${id}`, {
        replace: true,
      });
      return id;
    } catch (err) {
      logger.error("Error creating conversation", err);
      throw err;
    }
  };

  return {
    onUserUpdateMessage,
    isLoading,
    loadingType,
    sendRating,
    character,
    conversation: stateConversation,
    currentConversationId: stateConversationId,
    createConversation,
    isTyping: stateIsTyping,
    isLoadingMessages,
    generate,
    altMsgs: stateAltMsgs,
    altMsgPosition: stateAltMsgPosition,
    setAltMsgPosition: setStateAltMsgPosition,
    changeAltMsgPosition,
    onNewConversation,
    errorCode,
    errorMessage,
    errorType,
    errorTitle,
    resetError,
    removeMessages,
    deleteSingleMessage,
    isRemoveMessageLoading,
    removeMessageIdx: stateRemoveMessageIdx,
    setRemoveMessageIdx: setStateRemoveMessageIdx,
    removalMode: stateIsRemovalMode,
    setRemovalMode: setStateIsRemovalMode,
    setConversation: setStateConversation,
    setAltMsgs: setStateAltMsgs,
    loadMessages,
    lastMessageId: stateLastMessageId,
    cappedMessageId: stateCappedMessageId,
    loadMoreMessages,
    invalidUrlError: stateInvalidUrlError,
    handleCloneTitleUpdate,
    isCloneUpdateTitleLoading,
    conversationLabel: stateConversationLabel,
    setConversationLabel: setStateConversationLabel,
    isChoosePersona: stateIsChoosePersona,
    setIsChoosePersona: setStateIsChoosePersona,
    messagesLoading: stateIsMessagesLoading,
    displayError,
    displaySuccess,
    isLastMessageIdDeleted: stateIsLastMessageIdDeleted,
    handleLoadedMessages,
    voiceId: stateVoiceId || character?.voice_id || "",
    setVoiceId: setStateVoiceId,
    setConversationId: setStateConversationId,
    handleDeleteMessageImage,
    isImageDeleteFromMessageLoading,
    conversationCreatedAt: stateConversationCreatedAt,
    conversationNumMessages: stateConversationNumMessages,
  };
}

export default useConversation;
